<template>
  <v-container fluid class="pa-0">
    <div class="w-full">
      <v-text-field v-show="false" :value="files" :rules="rules" />
      <div :id="`${fileInputKey}_vimeo_player`" class="d-none"></div>

      <v-file-input
        v-show="false"
        :id="fileInputKey"
        :accept="fileFormatStr"
        @change="_getUploadFile($event)"
        v-model="fileData"
      ></v-file-input>

      <v-btn
        class="upload-btn"
        :class="{
          'input-error': error,
        }"
        text
        depressed
        @click.prevent="_triggerUploadFile()"
        :disabled="disabled"
        :loading="$store.getters.isUploading"
        :color="error ? 'error' : 'primary'"
      >
        {{ $t(buttonText) }}
      </v-btn>
      <small v-if="error" class="d-block caption error--text mt-1">{{ $t('message.formRequired') }}</small>

      <template v-if="files.length > 0">
        <ul class="file-list">
          <li
            v-for="(item, i) in files"
            :key="i"
            :class="{ 'preview-file': _getPreviewImg(item), 'read-only': readonly }"
          >
            <img v-if="_getPreviewImg(item)" :src="_getPreviewImg(item)" @click="_previewFile(item)" />
            <a v-else @click.stop="_previewFile(item)">{{ item.name }}</a>
            <v-btn
              v-if="!readonly"
              text
              depressed
              x-small
              v-ripple="false"
              icon
              class="ml-4"
              @click.prevent="_removeFile(i)"
              :disabled="disabled"
            >
              <v-icon :size="_getPreviewImg(item) ? 20 : 15" color="error" class="fi fi-br-cross-small"></v-icon>
            </v-btn>
          </li>
        </ul>
        <template v-if="single && isVimeoOnly">
          <small class="colorBlack--text" v-if="vimeoNotReady && duration <= 0">
            {{ $t('message.videoNotReady') }}
          </small>
          <div class="colorGrey--text" v-else>{{ $t('videoDuration') }}: {{ gfConvertDuration(duration) }}</div>
        </template>
      </template>
    </div>

    <!-- Modal: 影片名稱 -->
    <FormDialog v-if="acceptVimeo" title="videoName" :maxWidth="500" ref="videoNameDialog" formRef="videoNameForm">
      <v-row class="ma-0">
        <v-col cols="12">
          <FormInput label="videoName" :fieldValue.sync="videoName" required />
        </v-col>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import FormInput from '@/components/formField/FormInput.vue'
import FormDialog from '@/components/FormDialog.vue'

export default {
  name: 'FormUploader',
  components: {
    FormInput,
    FormDialog,
  },
  props: {
    fileInputKey: {
      type: String,
      default: 'upload_file',
      required: true,
    },
    files: {
      type: Array, // { name: '', file: null, url: '', video_id: null, duration: 0, is_vimeo: false }
      default: () => [],
      required: true,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    acceptFormat: {
      type: Array, // 'image' | 'video' | 'vimeo' | 'pdf'
      default: () => ['image'],
      required: false,
    },
    uploadText: {
      type: String,
      require: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    single: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    enablePreview: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    fileFormatStr() {
      return this.$common.getAcceptFileFormat(this.acceptFormat).formatStr
    },
    disabled() {
      return this.readonly || this.$store.getters.isLoading
    },
    buttonText() {
      if (this.$validate.DataValid(this.uploadText)) {
        return this.uploadText
      } else {
        if (this.acceptFormat.length === 1 && (this.acceptFormat[0] === 'video' || this.acceptFormat[0] === 'vimeo')) {
          return 'uploadVideo'
        }

        return 'upload'
      }
    },
    isVimeoOnly() {
      return this.acceptFormat.length === 1 && this.acceptFormat[0] === 'vimeo'
    },
    acceptVimeo() {
      return this.acceptFormat.includes('vimeo')
    },
  },
  watch: {
    files: {
      async handler(val) {
        this.duration = 0
        if (this.single && this.isVimeoOnly && this.$validate.DataValid(val, true)) {
          const item = val[0]
          if (!item.duration) {
            this.duration = await this._getVideoDuration(item)
            if (this.duration > 0) {
              let fileList = JSON.parse(JSON.stringify(this.files))
              fileList[0].duration = this.duration
              this.$emit('update:files', fileList)
            }
          } else {
            this.duration = item.duration
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  data: () => ({
    fileData: null,

    error: false,

    init: false,
    rules: [],

    // -- vimeo
    duration: 0,
    videoName: '',
    vimeoPlayer: null,
    vimeoNotReady: false,
    vimeoPlayerCont: null,
  }),
  methods: {
    reset() {
      this.error = false
      this.init = false
      this.rules = []
      this.fileData = null
      setTimeout(() => {
        this.rules = [v => this._check(v)]
      }, 500)
    },

    _check(value) {
      this.error = false
      if (this.init && this.required) {
        if (this.$validate.DataValid(value, true)) {
          for (let i = 0; i < value.length; i++) {
            if ((value[i].is_vimeo && !value[i].video_id) || (!value[i].is_vimeo && !value[i].file)) {
              this.error = true
              break
            }
          }
        } else {
          this.error = true
        }
      }
      this.init = true
      return !this.error
    },

    _triggerUploadFile() {
      if (this.$store.getters.isLoading || this.$store.getters.isUploading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      if (this.disabled) return

      try {
        document.getElementById(this.fileInputKey).click()
      } catch (err) {
        this.$common.error(err)
      }
    },
    async _getUploadFile(file) {
      if (file) {
        if (this.$store.getters.isLoading || this.$store.getters.isUploading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        const format = this.$common.getAcceptFileFormat(this.acceptFormat).formatArr
        const check = this.$validate.validateFileInput(file, format, this.acceptVimeo)
        if (this.$validate.DataValid(check)) {
          this.$store.dispatch('toggleSnack', {
            message: this.$t(check.message, check.value || {}),
            type: 'error',
          })
        } else {
          let isVimeo = false
          if (this.acceptVimeo) {
            const fileType = file.type.split('/')[1]
            if (['mp4', 'webm'].includes(fileType)) {
              isVimeo = true
            }
          }

          if (isVimeo) {
            const names = file.name.split('.')
            names.pop()
            this.videoName = names.join('.')
            this.handleAddVideoName(file)
          } else {
            this.$store.dispatch('setUploadLoading', true)
            try {
              const data = await this.$Fetcher.UploadFileNew(file)
              let fileList = JSON.parse(JSON.stringify(this.files))
              if (this.single) {
                fileList = []
              }
              fileList.push({ name: file.name, file: data })
              this.$emit('update:files', fileList)
              this.$emit('changed', fileList)
              this.error = false

              if (this.dispatchUpdateOnChange === true) {
                this.$store.dispatch('setDataIsUpdated', true)
              }

              this.$store.dispatch('toggleSnack', {
                message: 'message.uploadSuccess',
                type: 'success',
              })
            } catch (err) {
              this.$common.error(err)
              this.$store.dispatch('toggleSnack', {
                message: err,
                type: 'error',
              })
            } finally {
              this.fileData = null
              this.$store.dispatch('setUploadLoading', false)
            }
          }
        }
      }
    },

    _getPreviewImg(item) {
      if (this.enablePreview) {
        if (!item.is_vimeo) {
          const type = item.file
            .split('.')
            .pop()
            .toLowerCase()
          if (['png', 'jpeg', 'jpg'].includes(type)) {
            return `${this.$mediaPath}${item.file}`
          }
        }
      }

      return null
    },

    _previewFile(item) {
      if (item.is_vimeo && item.url) {
        this.$store.dispatch('openMediaPopup', { media: item.url, isVideo: true })
      } else if (item.file && typeof item.file === 'string') {
        const url = `${this.$mediaPath}${item.file}`
        const type = item.file
          .split('.')
          .pop()
          .toLowerCase()

        if (['png', 'jpeg', 'jpg'].includes(type)) {
          this.$store.dispatch('openMediaPopup', { media: url })
        } else if (['mp4', 'webm'].includes(type)) {
          this.$store.dispatch('openMediaPopup', { media: url, isVideo: true })
        } else {
          window.open(url, '_blank')
        }
      }
    },

    async _removeFile(index) {
      if (this.$store.getters.isLoading || this.$store.getters.isUploading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }
      if (this.disabled) return

      this.$store.dispatch('setLoading', true)
      const item = this.files[index]
      if (item.is_vimeo && item.video_id) {
        await this._removeVimeo(item.video_id)
      }
      this.fileData = null
      const fileList = JSON.parse(JSON.stringify(this.files))
      fileList.splice(index, 1)
      this.$emit('update:files', fileList)
      this.$emit('changed', fileList)

      if (this.required && fileList.length < 1) {
        this.error = true
      } else {
        this.error = false
      }

      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
      this.$store.dispatch('setLoading', false)
    },

    // ------------- Vimeo upload -------------
    async handleAddVideoName(file) {
      this.$refs.videoNameDialog.setLoading(false)
      const confirm = await this.$refs.videoNameDialog.show()
      if (confirm === true) {
        this.$store.dispatch('setUploadLoading', true)
        try {
          // Step 1: create vimeo link
          const data = await this._createVimeo(file)

          // Step 2: upload link to vimeo
          await this.uploadVimeoVideo(file, data.upload_link)

          // Step 3: remove previous uploaded vimeo if is single upload
          if (this.single) {
            if (this.$validate.DataValid(this.files, true) && this.files[0].video_id) {
              await this._removeVimeo(this.files[0].video_id)
            }
          }

          // Step 4: update newest value & alert success
          const value = {
            url: data.uri,
            file: file,
            video_id: data.video_id,
            name: data.file_name,
            duration: 0,
            is_vimeo: true,
          }
          value['duration'] = await this._getVideoDuration(value)

          let fileList = JSON.parse(JSON.stringify(this.files))
          if (this.single) {
            fileList = []
          }
          fileList.push(value)
          this.$emit('update:files', fileList)
          this.$emit('changed', fileList)
          this.error = false

          if (this.dispatchUpdateOnChange === true) {
            this.$store.dispatch('setDataIsUpdated', true)
          }

          this.$store.dispatch('toggleSnack', {
            message: 'message.uploadSuccess',
            type: 'success',
          })

          this.$refs.videoNameDialog.hide()
        } catch (err) {
          this.$common.error(err)
          let msg = err
          let logout = false;
          switch (err) {
            case 'invalid access token':
            case 'refresh token expired':
            case 'invalid refresh token':
            case 'invalid refresh token payload':
            case 'not a refresh token':
            case 'invalid access token payload':
            case 'not a access token':
            case 'access token and refresh token is not a pair':
            case 'value missing':
              logout = true
              msg = `${this.$t('message.loginExpired')} (${err.toUpperCase()})`
              break
            default:
              if (typeof err !== 'string' && err !== null) {
                msg = JSON.stringify(err)
              }
          }
          this.showAlert({
            title: 'message.uploadFail',
            message: msg,
            isError: true,
            returnLink: logout ? 'Login' : null
          })
          this.handleAddVideoName(file)
        } finally {
          this.$store.dispatch('setUploadLoading', false)
        }
      } else {
        this.fileData = null
      }
    },
    _createVimeo(file) {
      return new Promise(async (resolve, reject) => {
        try {
          const getUser = this.gfGetCurrentUserData()
          const data = await this.$Fetcher.CreateVimeoVideo(file, {
            access_token: getUser.access_token,
            refresh_token: getUser.refresh_token,
            custom_video_title: this.videoName || file.name,
          })
          // save the new access token (if any)
          if (this.$validate.DataValid(data.access_token)) {
            this.gfUpdateUserAccessToken(data.access_token)
          }
          this.$store.dispatch('insertRemovedVimeoVideoID', data.video_id)
          resolve(data)
        } catch (err) {
          this.$common.error(err)
          reject(err)
        }
      })
    },
    _removeVimeo(videoId) {
      return new Promise(async (resolve, reject) => {
        try {
          if (this.$store.getters.removedVimeoVideoIDList.includes(videoId)) {
            const getUser = this.gfGetCurrentUserData()
            const data = await this.$Fetcher.DeleteUnusedVimeoVideo(
              [videoId],
              getUser.access_token,
              getUser.refresh_token,
            )
            // save the new access token (if any)
            if (this.$validate.DataValid(data.access_token)) {
              this.gfUpdateUserAccessToken(data.access_token)
            }

            this.$store.dispatch('removeRemovedVimeoVideoID', videoId)
          }

          resolve(true)
        } catch (err) {
          this.$common.error(err)
          resolve(false)
        }
      })
    },

    async _getVideoDuration(obj) {
      this.vimeoNotReady = false
      if (this.$validate.DataValid(obj)) {
        if (this.$validate.DataValid(obj.url)) {
          if (this.vimeoPlayer !== null) {
            try {
              await this.vimeoPlayer.destroy()
            } catch {}

            this.vimeoPlayer = null
          }

          try {
            if (!this.vimeoPlayerCont) {
              const Player = await import('@vimeo/player')
              this.vimeoPlayerCont = Player.default
            }

            if (!this.vimeoPlayerCont) {
              throw 'no player'
            }
            this.vimeoPlayer = new this.vimeoPlayerCont(`${this.fileInputKey}_vimeo_player`, { url: obj.url })

            const duration = await this.vimeoPlayer.getDuration()
            return duration.toFixed(0)
          } catch (err) {
            this.$common.error(err)
            this.vimeoNotReady = true
            return await this._getVideoDurationFromFile(obj)
          }
        } else {
          return await this._getVideoDurationFromFile(obj)
        }
      }
      return 0
    },
    _getVideoDurationFromFile(obj) {
      return new Promise(resolve => {
        if (this.$validate.DataValid(obj) && this.$validate.DataValid(obj.file)) {
          const video = document.createElement('video')
          video.muted = true
          video.src = URL.createObjectURL(obj.file)
          video.preload = 'metadata'
          video.onloadedmetadata = function() {
            resolve(video.duration.toFixed(0))
          }
          video.onerror = function(ev) {
            this.vimeoNotReady = true
            resolve(0)
          }
        } else {
          this.vimeoNotReady = true
          resolve(0)
        }
      })
    },
  },
  async created() {
    this.reset()

    if (this.acceptVimeo) {
      const Player = await import('@vimeo/player')
      this.vimeoPlayerCont = Player.default
    }
  },
  async beforeDestroy() {
    if (this.vimeoPlayer !== null) {
      try {
        this.vimeoPlayer.destroy()
      } catch (err) {
        this.$common.error(err)
      }
      this.vimeoPlayer = null
    }
  },
}
</script>
